import ModuleBase from '../shared/ModuleBase.js';

/**
 * Navigation — Desktop Flyout + Mobile Toggle
 * Desktop: Hover-Flyout mit Keyboard-Support (Escape, ArrowDown/Up, Tab)
 * Mobile: Klick-Toggle fuer Submenues, Escape/Close, ARIA
 */
export default class Navigation extends ModuleBase {
    constructor(el) {
        super(el);
        this._initMobile();
        this._initDesktopFlyouts();
        this._initGlobalEscape();
        this._initOutsideClick();
    }

    _initMobile() {
        this._mobileContainers = this.$el.querySelectorAll('[data-megamenu="container"]');

        this._mobileContainers.forEach((container) => {
            const trigger = container.querySelector('[data-megamenu="trigger"]');
            if (trigger) {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this._isMobileOpen(container) ? this._closeMobile(container) : this._openMobile(container);
                });
            }
        });
    }

    _openMobile(container) {
        const trigger = container.querySelector('[data-megamenu="trigger"]');
        const panel = container.querySelector('[data-megamenu="panel-mobile"]');
        const chevron = container.querySelector('[data-megamenu="chevron"]');
        if (panel) panel.style.display = '';
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        if (chevron) chevron.classList.add('rotate-180');
    }

    _closeMobile(container) {
        const trigger = container.querySelector('[data-megamenu="trigger"]');
        const panel = container.querySelector('[data-megamenu="panel-mobile"]');
        const chevron = container.querySelector('[data-megamenu="chevron"]');
        if (panel) panel.style.display = 'none';
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (chevron) chevron.classList.remove('rotate-180');
    }

    _isMobileOpen(container) {
        const trigger = container.querySelector('[data-megamenu="trigger"]');
        return trigger && trigger.getAttribute('aria-expanded') === 'true';
    }

    _initDesktopFlyouts() {
        this._flyoutContainers = this.$el.querySelectorAll('[data-flyout="container"]');

        this._flyoutContainers.forEach((container) => {
            const trigger = container.querySelector('[data-flyout="trigger"]');
            const panel = container.querySelector('[data-flyout="panel"]');
            let closeTimer = null;

            container.addEventListener('mouseenter', () => {
                clearTimeout(closeTimer);
                this._openFlyout(container);
            });

            container.addEventListener('mouseleave', () => {
                closeTimer = setTimeout(() => {
                    this._closeFlyout(container);
                }, 150);
            });

            if (trigger) {
                trigger.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this._isFlyoutOpen(container) ? this._closeFlyout(container) : this._openFlyout(container);
                        if (!this._isFlyoutOpen(container)) return;
                        const firstItem = panel ? panel.querySelector('[role="menuitem"]') : null;
                        if (firstItem) firstItem.focus();
                    }
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        this._openFlyout(container);
                        const firstItem = panel ? panel.querySelector('[role="menuitem"]') : null;
                        if (firstItem) firstItem.focus();
                    }
                });
            }

            if (panel) {
                panel.addEventListener('keydown', (e) => {
                    const items = panel.querySelectorAll('[role="menuitem"]');
                    const currentIndex = Array.prototype.indexOf.call(items, document.activeElement);

                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const next = currentIndex + 1 < items.length ? currentIndex + 1 : 0;
                        items[next].focus();
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const prev = currentIndex - 1 >= 0 ? currentIndex - 1 : items.length - 1;
                        items[prev].focus();
                    }
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        this._closeFlyout(container);
                        if (trigger) trigger.focus();
                    }
                    if (e.key === 'Tab') {
                        const isLast = currentIndex === items.length - 1 && !e.shiftKey;
                        const isFirst = currentIndex === 0 && e.shiftKey;
                        if (isLast || isFirst) {
                            this._closeFlyout(container);
                        }
                    }
                });
            }
        });
    }

    _openFlyout(container) {
        const trigger = container.querySelector('[data-flyout="trigger"]');
        const panel = container.querySelector('[data-flyout="panel"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        if (panel) {
            panel.classList.remove('invisible', 'opacity-0');
            panel.classList.add('visible', 'opacity-100');
        }
    }

    _closeFlyout(container) {
        const trigger = container.querySelector('[data-flyout="trigger"]');
        const panel = container.querySelector('[data-flyout="panel"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (panel) {
            panel.classList.add('invisible', 'opacity-0');
            panel.classList.remove('visible', 'opacity-100');
        }
    }

    _isFlyoutOpen(container) {
        const trigger = container.querySelector('[data-flyout="trigger"]');
        return trigger && trigger.getAttribute('aria-expanded') === 'true';
    }

    _initGlobalEscape() {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this._mobileContainers.forEach((c) => this._closeMobile(c));
                const checkbox = this.$el.querySelector('[id^="mobile-menu-toggle-"]');
                if (checkbox && checkbox.checked) checkbox.checked = false;
                this._flyoutContainers.forEach((c) => this._closeFlyout(c));
            }
        });
    }

    _initOutsideClick() {
        document.addEventListener('click', (e) => {
            this._flyoutContainers.forEach((container) => {
                if (!container.contains(e.target)) {
                    this._closeFlyout(container);
                }
            });
        });
    }
}
